/**
 * Transportart-Kategorisierung der Bestellungen (See/Flug/Strasse) - Grundlage des Transport-Icons
 * vor dem Lieferantennamen (`components/Board/Bestellzeile.tsx`) und des app-weiten Transportart-
 * Filters (`components/App/Kopfzeile.tsx`).
 *
 * EIGENE DATEI, NICHT in `lib/laender.ts`: deren eigener Kopfkommentar ("Laenderkategorisierung der
 * Lieferanten...") ist ausdruecklich laenderspezifisch (Herkunft ueber `Supplier.iso`) - diese Regel
 * hier braucht dagegen gar keinen Lieferanten-Nachschlag, sie liest `Order.transportMode` direkt.
 *
 * ================= Die geteilte Sichtbarkeitsregel (analog zu `laender.ts`) =====================
 *
 * Vorbild ist `istBestellungNachLaenderFilterSichtbar` unten in derselben Bauart: EINE Regel fuer
 * alle fuenf bestellungsfuehrenden Seiten (Board, Uebersicht, Zeitstrahl, Zahlungen, Kosten), Zustand
 * und Bedienung in der app-weiten Kopfzeile (`App.tsx`/`Kopfzeile.tsx`).
 *
 * ANDERS als beim Laenderfilter gibt es hier KEINEN vierten Fall wie `"other"`: jede Bestellung ist
 * See, Flug ODER Strasse (Feature "See/Flug/Strasse-Umschaltung", `order_extras.transport_mode`), nie
 * unbestimmt - der DB-Default ist `'see'` (`lib/types.ts#Order.transportMode`), und jeder unbekannte
 * oder fehlende Wert zaehlt ebenfalls als `'see'`.
 */
import type { Order } from "./types.ts";

/**
 * Der Luftfracht/Seefracht/Strassen-Filter der Kopfzeile. Bis zum KI-Zugang (Stufe 1) stand der Typ in
 * `state/arbeitsplatz.ts`; er wohnt hier, weil `kostenAuswertung`/`uebersichtAuswertung` ihn in ihrer
 * Eingabe tragen und der Server kein Zustandsmodul (`localStorage`) in der Importkette haben darf.
 * `state/arbeitsplatz.ts` fuehrt den Namen als Re-Export weiter.
 */
export type TransportartFilter = { see: boolean; air: boolean; road: boolean };

/**
 * Die Vorgabe „alle drei Transportarten sichtbar" - dieselbe Bauart wie `LAENDERFILTER_VORGABE` in
 * `laender.ts`. Der Parametertyp ist bewusst STRUKTURELL, aus demselben Grund: `lib/` haengt nicht von
 * `state/` ab (die Abhaengigkeit laeuft immer andersherum).
 *
 * NAMENSANGLEICHUNG (Nachbesserung, Befund 6e): hiess bis dahin `TRANSPORTART_FILTER_VORGABE` -
 * eine Schreibweise mit Unterstrich, die `LAENDERFILTER_VORGABE` (ohne Unterstrich zwischen
 * `LAENDER` und `FILTER`) nicht mitgeht. Umbenannt bei derselben Gelegenheit, bei der Punkt 3 der
 * Nachbesserung ohnehin jede Fundstelle anfasst (Pflicht-Prop auf allen fuenf Seiten).
 */
export const TRANSPORTARTFILTER_VORGABE: TransportartFilter = {
  see: true,
  air: true,
  road: true,
};

/**
 * Die Transportart einer Bestellung: `"air"` und `"road"` wie in `order.transportMode`, alles andere (`"see"`, fehlend, unbekannt oder
 * keine Bestellung) ist `"see"`. Die EINE Moduswahl: der Filter unten und die KI-Werkzeuge des Servers (ueber die Kopie) rufen sie.
 */
export function transportModus(order: Order | null | undefined): "see" | "air" | "road" {
  return order?.transportMode === "air" ? "air" : order?.transportMode === "road" ? "road" : "see";
}

/**
 * Ob eine Bestellung nach dem Luftfracht/Seefracht/Strassenfracht-Filter sichtbar bleibt - die EINE
 * Regel fuer alle fuenf bestellungsfuehrenden Seiten (siehe Kopfkommentar): ausgeblendet wird, wenn
 * `order.transportMode` `"see"` ist (oder fehlt/`undefined`/ein unbekannter Wert ist - der DB-Default)
 * UND `filter.see === false`, bzw. `"air"` ist UND `filter.air === false`, bzw. `"road"` ist UND
 * `filter.road === false`.
 *
 * Der Parametertyp des Filters ist bewusst STRUKTURELL (`{ see; air; road }`) statt ein Import von
 * `state/arbeitsplatz.ts#TransportartFilter` - derselbe Grund wie bei
 * `istBestellungNachLaenderFilterSichtbar` in `laender.ts`.
 */
export function istBestellungNachTransportartFilterSichtbar(
  order: Order | null | undefined,
  filter: TransportartFilter,
): boolean {
  const modus = transportModus(order);
  if (modus === "see" && !filter.see) return false;
  if (modus === "air" && !filter.air) return false;
  if (modus === "road" && !filter.road) return false;
  return true;
}
